import { Fragment, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useDateSort } from '../../../../../hooks/useDateSort';

export type RouletteConversionStepsRow = {
  id: number;
  date: string;
  totalCount: number;
  totalRate: number;
  steps: { count: number; rate: number }[];
};

const STEP_NUMBERS = ['❶', '❷', '❸', '❹', '❺', '❻', '❼', '❽', '❾', '❿', '⓫', '⓬', '⓭'];

const headerCellSx = { fontWeight: 600, whiteSpace: 'nowrap' as const, borderLeft: '2px solid #e5e7eb', fontSize: '1.05rem' };
const subHeaderRowCellSx = { fontWeight: 600, whiteSpace: 'nowrap' as const, fontSize: '0.8rem' };

function formatCount(value: number): string {
  if (!Number.isFinite(value)) return '-';
  if (value === 0) return '0';
  return value.toLocaleString('ko-KR');
}

function formatRate(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(2)}%`;
}

type Props = {
  stepCount: number;
  rows: RouletteConversionStepsRow[];
  compareRows?: RouletteConversionStepsRow[];
};

function StepCells({ steps, count }: { steps: { count: number; rate: number }[]; count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <Fragment key={i}>
          <TableCell align="center">{formatCount(steps[i]?.count ?? 0)}</TableCell>
          <TableCell align="center">{formatRate(steps[i]?.rate ?? 0)}</TableCell>
        </Fragment>
      ))}
    </>
  );
}

export default function RouletteConversionStepsTable({ stepCount, rows, compareRows }: Props) {
  const { sorted, sortDirection, toggleSort } = useDateSort(rows, undefined, 'desc');
  const sortedCompare = useMemo(() => {
    if (!compareRows || compareRows.length !== rows.length) return [];
    const dir = sortDirection === 'desc' ? 1 : -1;
    return [...compareRows].sort((a, b) => dir * b.date.localeCompare(a.date));
  }, [compareRows, rows.length, sortDirection]);
  const displayRows = useMemo(() => {
    if (sortedCompare.length === 0) return sorted.map((r) => ({ row: r, periodType: 'base' as const }));
    return sorted.flatMap((row, i) => [
      { row, periodType: 'base' as const },
      { row: sortedCompare[i], periodType: 'compare' as const },
    ]).filter((x) => x.row != null);
  }, [sorted, sortedCompare]);
  return (
    <Box sx={{ backgroundColor: '#ffffff', p: 0, mt: 3, overflow: 'auto' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                align="center"
                onClick={toggleSort}
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  borderRight: '2px solid #e5e7eb',
                  verticalAlign: 'middle',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  날짜
                  <Box component="span" sx={{ fontSize: '0.75rem', color: '#666' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </Box>
                </Box>
              </TableCell>
              <TableCell align="center" colSpan={2} sx={headerCellSx}>
                전체
              </TableCell>
              {STEP_NUMBERS.slice(0, stepCount).map((num) => (
                <TableCell key={num} align="center" colSpan={2} sx={headerCellSx}>{num}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell align="center" sx={subHeaderRowCellSx}>최종 전환 수</TableCell>
              <TableCell align="center" sx={subHeaderRowCellSx}>최종 전환율</TableCell>
              {Array.from({ length: stepCount }, (_, i) => (
                <Fragment key={i}>
                  <TableCell align="center" sx={subHeaderRowCellSx}>이벤트 수</TableCell>
                  <TableCell align="center" sx={subHeaderRowCellSx}>전환율</TableCell>
                </Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map(({ row, periodType }) => (
              <TableRow
                key={`${periodType}-${row.date}-${row.id}`}
                sx={sortedCompare.length > 0 && periodType === 'base' ? { '& td': { borderBottom: 'none' } } : undefined}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                <TableCell align="center">{formatCount(row.totalCount)}</TableCell>
                <TableCell align="center">{formatRate(row.totalRate)}</TableCell>
                <StepCells steps={row.steps} count={stepCount} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
